/* global Office */

import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from "@azure/msal-browser";
import {getRuntimeConfig} from "./services/runtimeConfig";

const LEGACY_API_KEY_STORAGE_KEY = "librechat_api_key";

function getRuntimeValue(name: string): string {
  const value =
    getRuntimeConfig()[name as keyof ReturnType<typeof getRuntimeConfig>] ??
    process.env[name] ??
    "";
  return typeof value === "string" ? value : "";
}

export function getRuntimeAccessError(): string {
  const missing = [
    "ENTRA_CLIENT_ID",
    "ENTRA_TENANT_ID",
    "ENTRA_API_SCOPE",
  ].filter((key) => !getRuntimeValue(key));

  if (missing.length === 0) return "";
  return `Missing runtime Entra configuration: ${missing.join(", ")}.`;
}

export function isNaaSupported(): boolean {
  try {
    return Boolean(
      typeof Office !== "undefined" &&
      Office.context &&
      Office.context.requirements &&
      Office.context.requirements.isSetSupported("NestedAppAuth", "1.1"),
    );
  } catch {
    return false;
  }
}

let msalInstance: IPublicClientApplication | null = null;
let msalInitPromise: Promise<IPublicClientApplication> | null = null;

function requireConfig(name: string): string {
  const value = getRuntimeValue(name);
  if (!value) {
    throw new Error(
      `Missing ${name}. Set it in your environment / .env before building.`,
    );
  }
  return value;
}

async function initMsal(): Promise<IPublicClientApplication> {
  if (msalInstance) return msalInstance;
  if (!msalInitPromise) {
    const config = {
      auth: {
        clientId: requireConfig("ENTRA_CLIENT_ID"),
        authority: `https://login.microsoftonline.com/${requireConfig("ENTRA_TENANT_ID")}`,
      },
      cache: {
        cacheLocation: "localStorage",
      },
    };
    msalInitPromise = createNestablePublicClientApplication(config);
  }

  const client = await msalInitPromise;
  msalInstance = client;
  return client;
}

export async function getAccessToken({
  allowInteractive = false,
}: {allowInteractive?: boolean} = {}): Promise<string> {
  if (!isNaaSupported()) {
    throw new Error(
      "This Office host does not support Nested App Authentication.",
    );
  }

  const scope = requireConfig("ENTRA_API_SCOPE");
  const client = await initMsal();
  if (!client) {
    throw new Error("MSAL client is not available.");
  }
  const activeAccount = client.getActiveAccount?.();
  const request = {scopes: [scope] as string[]};
  if (activeAccount) {
    (request as {account?: unknown}).account = activeAccount;
  }

  try {
    const result = await client.acquireTokenSilent(request);
    if (result.account) client.setActiveAccount(result.account);
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError && allowInteractive) {
      const result = await client.acquireTokenPopup(request);
      if (result.account) client.setActiveAccount(result.account);
      return result.accessToken;
    }
    throw err;
  }
}

export async function getSignedInUser(): Promise<string | null> {
  const client = await initMsal();
  if (!client) {
    return null;
  }
  const account =
    client.getActiveAccount?.() || (client.getAllAccounts?.() || [])[0];
  return account ? account.username || null : null;
}

export async function signIn(): Promise<string> {
  const scope = requireConfig("ENTRA_API_SCOPE");
  const client = await initMsal();
  if (!client) {
    throw new Error("MSAL client is not available.");
  }
  const result = await client.acquireTokenPopup({
    scopes: [scope],
    prompt: "login",
  });
  if (result.account) client.setActiveAccount(result.account);
  return result.accessToken;
}

export async function signOut(): Promise<void> {
  const client = await initMsal();
  if (!client) {
    return;
  }
  client.setActiveAccount(null);
}

export function clearLegacyApiKey(): void {
  try {
    localStorage.removeItem(LEGACY_API_KEY_STORAGE_KEY);
  } catch {
    // Ignore storage access failures — the app can still continue with the token.
  }
}
